import type { AssessmentKind, GradingRule, Rounding } from './rules';
import { type GradeValue, type SpecialMark, levelFromOrdinal, levelOrdinal } from './scales';

/** A summative grade used as input for a final grade. Formative feedback is a different type. */
export interface SummativeInput {
  kind: AssessmentKind;
  value: GradeValue | null;
  special?: SpecialMark;
  /** used by the 'weighted' method; defaults to 1 */
  weight?: number;
}

export type FinalTarget = 'thematic' | 'semester' | 'year';

export interface FinalSuggestion {
  value: GradeValue;
  /** unrounded numeric result, kept for transparency in the UI («чому така підказка») */
  raw: number;
  method: 'mean' | 'median' | 'weighted';
  basedOn: { kinds: AssessmentKind[]; count: number };
}

/**
 * Which inputs a final grade is derived from:
 * - thematic ← current + test grades of the topic;
 * - semester ← thematic grades; if there are none, ← current + test (order №1427);
 * - year ← semester grades.
 */
function selectBasis(target: FinalTarget, inputs: ReadonlyArray<SummativeInput>): SummativeInput[] {
  const of = (...kinds: AssessmentKind[]) => inputs.filter((i) => kinds.includes(i.kind));
  switch (target) {
    case 'thematic':
      return of('current', 'test');
    case 'semester': {
      const thematic = of('thematic');
      return thematic.length > 0 ? thematic : of('current', 'test');
    }
    case 'year':
      return of('semester');
  }
}

function round(value: number, rounding: Rounding): number {
  switch (rounding) {
    case 'floor':
      return Math.floor(value);
    case 'ceil':
      return Math.ceil(value);
    case 'half_up':
      // epsilon: weighted averages can yield 7.4999999999 for what is exactly 7.5
      return Math.floor(value + 0.5 + 1e-9);
  }
}

function aggregate(
  values: ReadonlyArray<{ n: number; weight: number }>,
  method: 'mean' | 'median' | 'weighted',
): number {
  if (method === 'median') {
    const sorted = values.map((v) => v.n).sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 === 1
      ? (sorted[mid] as number)
      : ((sorted[mid - 1] as number) + (sorted[mid] as number)) / 2;
  }
  if (method === 'weighted') {
    const totalWeight = values.reduce((s, v) => s + v.weight, 0);
    return values.reduce((s, v) => s + v.n * v.weight, 0) / totalWeight;
  }
  return values.reduce((s, v) => s + v.n, 0) / values.length;
}

/**
 * Suggests a final grade. Returns null when the system should not suggest anything:
 * the rule says «teacher decides», the scale is verbal/pass-fail, inputs are mixed
 * scales, or there is nothing to aggregate. The teacher always confirms the grade.
 */
export function suggestFinal(
  target: FinalTarget,
  inputs: ReadonlyArray<SummativeInput>,
  rule: GradingRule,
): FinalSuggestion | null {
  const method = rule.finalMethod ?? 'teacher';
  if (method === 'teacher') return null;
  if (rule.scale !== 'points12' && rule.scale !== 'levels4') return null;

  const basis = selectBasis(target, inputs).filter(
    (i): i is SummativeInput & { value: GradeValue } => i.value !== null && i.special === undefined,
  );
  if (basis.length === 0) return null;

  const numeric: { n: number; weight: number }[] = [];
  for (const input of basis) {
    const weight = input.weight ?? 1;
    if (rule.scale === 'points12' && input.value.scale === 'points12') {
      numeric.push({ n: input.value.points, weight });
    } else if (rule.scale === 'levels4' && input.value.scale === 'levels4') {
      numeric.push({ n: levelOrdinal(input.value.level), weight });
    } else {
      // mixed scales (e.g. verbal current grades → level final): the teacher decides
      return null;
    }
  }

  const raw = aggregate(numeric, method);
  const rounded = round(raw, rule.rounding ?? 'half_up');
  const value: GradeValue =
    rule.scale === 'points12'
      ? { scale: 'points12', points: Math.min(12, Math.max(1, rounded)) }
      : { scale: 'levels4', level: levelFromOrdinal(Math.min(4, Math.max(1, rounded))) };

  return {
    value,
    raw,
    method,
    basedOn: { kinds: [...new Set(basis.map((b) => b.kind))], count: basis.length },
  };
}
